import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { formatTimeAgo } from '@cdw-ae/date-time-service';
import {
  selectCacheLastUpdated,
  selectFormattedDeviceCount,
  selectFormattedGroupingsCount,
  selectFormattedUserCount,
  selectIsFullAssetSyncRunning,
} from '@cdw-ae/jobs-status-state';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { selectHeaderText } from '../../+state/app-settings.selectors';
import { UserData } from '../../+state/user-identity/user-identity-settings.models';
import { selectCurrentUserData } from '../../+state/user-identity/user-identity-settings.selectors';
import { RootState } from '../../common/interfaces/rootState.interface';
import { LoginVerdictStateService } from '../../common/services/userIdentity/login-verdict-state.service';
import { SpogFooterComponent } from '../common-components/footer/footer.component';
import { AppHeaderComponent } from './components/app-header/app-header.component';
import { NavbarActionButtonsComponent } from './components/navbar-action-buttons/navbar-action-buttons.component';
import { NavbarButtonsRegistryService } from './components/navbar-action-buttons/services/navbar-buttons-registry.service';
import { JobsPanelComponent } from './components/sidebar-navigation/job-indicator/jobs-panel/jobs-panel.component';
import { SidebarNavigationComponent } from './components/sidebar-navigation/sidebar-navigation.component';

@Component({
  selector: 'cdw-internal-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarNavigationComponent,
    SpogFooterComponent,
    AppHeaderComponent,
    NavbarActionButtonsComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    JobsPanelComponent,
  ],
  templateUrl: './internal-layout.component.html',
  styleUrls: ['./internal-layout.component.scss'],
})
export class InternalLayoutComponent implements OnInit, OnDestroy {
  private store: Store<RootState> = inject(Store);
  private navbarButtonsRegistry = inject(NavbarButtonsRegistryService);
  private readonly loginVerdictState = inject(LoginVerdictStateService);
  private destroy$ = new Subject<void>();

  // Jobs panel state
  jobsPanelOpen = false;

  userData$: Observable<UserData | null> = this.store.select(
    selectCurrentUserData
  );
  headerText$: Observable<string | null> = this.store.select(selectHeaderText);

  /**
   * The footer's "License … expires on" line (EDUS-2810). Comes off the login
   * resolution, which the APP branch records on every sign-in and every hard
   * refresh; null until that round trip lands, and the footer hides the line
   * rather than showing a placeholder date.
   */
  protected readonly licenseExpiry = computed(
    () => this.loginVerdictState.state()?.context?.expiry ?? null
  );

  userInfo$: Observable<{ name: string; avatarUrl: string } | null> =
    this.userData$.pipe(
      map((userData) => {
        if (userData) {
          return {
            name: userData.name || userData.email || 'User',
            avatarUrl: userData.photoURL || 'images/gopher.svg',
          };
        }
        return null;
      })
    );

  // Sync indicator config for @for template loop
  readonly syncIndicators = [
    { icon: 'laptop', count$: this.store.select(selectFormattedDeviceCount) },
    { icon: 'people', count$: this.store.select(selectFormattedUserCount) },
    {
      icon: 'account_tree',
      count$: this.store.select(selectFormattedGroupingsCount),
    },
  ];

  // Full asset sync status from jobs NgRx state (Firestore query filters by numeric customerId)
  isRefreshing$: Observable<boolean> = this.store.select(
    selectIsFullAssetSyncRunning
  );

  // Last updated timestamp from jobs NgRx state (most recent finished execution)
  lastUpdated$: Observable<Date | null> = this.store.select(
    selectCacheLastUpdated
  );

  // Formatted timestamp using formatTimeAgo
  formattedLastUpdated$: Observable<string | null> = this.lastUpdated$.pipe(
    map((date) => {
      if (!date) return null;
      return formatTimeAgo(date);
    })
  );

  ngOnInit(): void {
    this.navbarButtonsRegistry.registerButtons();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleJobsPanel(): void {
    this.jobsPanelOpen = !this.jobsPanelOpen;
  }

  closeJobsPanel(): void {
    this.jobsPanelOpen = false;
  }
}
